/**
 * /documents — 我的笔记与文件. Private workspace for the logged-in
 * teacher: notes (read-only mock for now) + a real file list backed
 * by Nova OS document storage.
 *
 * Upload + list talk to /api/documents/* on Bosong nova-os, routed
 * through the school-bosong nginx vhost. The Bearer JWT in
 * Authorization is what scopes the teacher to their own files —
 * server-side Nova OS rewrites every path under users/<user_id>/ for
 * non-admin roles, so two teachers can't see each other's uploads
 * even if they tried.
 */
import { useRef, useState } from "react";
import { searchableTwinOf } from "../../lib/searchableTwin";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { authHeaders } from "../../api/school";
import { FilePreviewModal } from "./FilePreviewModal";
import { FolderPanel, PromoteFileButton } from "./FolderPanel";

interface DocEntry {
  name: string;
  path: string;
  type: "file" | "folder";
}

interface DocTreeFile {
  name: string;
  doc_id?: string | null;
  status?: string | null;
}

interface DocTree {
  path: string;
  folders?: string[];          // Nova OS returns folder names as bare strings
  files?: DocTreeFile[];       // file objects, no `path` / `size` key
}

async function fetchFiles(dir = ""): Promise<DocEntry[]> {
  const url = dir
    ? `/api/documents/tree?path=${encodeURIComponent(dir)}`
    : "/api/documents/tree";
  const r = await fetch(url, { headers: authHeaders() });
  if (!r.ok) throw new Error(`HTTP ${r.status}`);
  const body = (await r.json()) as DocTree;
  // Compose absolute path from tree.path + name. At root, tree.path is "".
  const join = (name: string) => (body.path ? `${body.path}/${name}` : name);
  // Folders with dedicated panels on this page (备课笔记/生成产物/教学计划)
  // are hidden from the root listing so each file surfaces exactly once —
  // artifacts showing up under "我的资料(个人知识库)" contradicted their
  // 不入库 badge (2026-07-28 ask). Deeper levels list everything.
  const dedicated = new Set(["notes", "artifacts", "teaching-plans"]);
  const folders: DocEntry[] = (body.folders ?? [])
    .filter((name) => dir !== "" || !dedicated.has(name))
    .map((name) => ({
      name,
      path: join(name),
      type: "folder",
    }));
  const files: DocEntry[] = (body.files ?? []).map((f) => ({
    name: f.name,
    path: join(f.name),
    type: "file",
  }));
  return [...folders, ...files];
}

async function uploadFile(file: File, dir = ""): Promise<void> {
  const fd = new FormData();
  fd.append("file", file);
  // Empty trailing path = user's home dir; a dir lands the upload under
  // that subdir (so uploading while inside a folder stays there). Nova OS
  // rewrites everything under users/<user_id>/ for non-admin roles.
  const path = dir ? `${dir}/` : "";
  const r = await fetch(`/api/documents/upload/${path}`, {
    method: "POST",
    headers: authHeaders(),
    body: fd,
  });
  if (!r.ok) {
    const body = (await r.json().catch(() => ({}))) as { detail?: string };
    throw new Error(body.detail ?? `HTTP ${r.status}`);
  }
}

async function deleteFile(path: string): Promise<void> {
  // DELETE /api/documents/file/<path>:删除文件并级联清检索块(/rm/
  // 不级联,勿改回 —— 2026-08-12 孤儿块事故)。Server-
  // side it asserts the resolved path stays inside the caller's
  // users/<user_id>/ scope (per-user storage), so we can't poke at
  // other teachers' files even with a forged path.
  const r = await fetch(`/api/documents/file/${encodeURIComponent(path)}`, {
    method: "DELETE",
    headers: authHeaders(),
  });
  if (!r.ok) {
    const body = (await r.json().catch(() => ({}))) as { detail?: string };
    throw new Error(body.detail ?? `HTTP ${r.status}`);
  }
}


export function NotesFilesPage(): JSX.Element {
  const { t } = useTranslation();
  return (
    <div className="mx-auto flex max-w-6xl flex-col gap-5 p-4 text-slate-800 md:p-6">
      <header>
        <p className="text-xs font-semibold uppercase text-slate-500 tracking-wide">
          {t("documents.eyebrow")}
        </p>
        <h1 className="mt-1 text-2xl font-semibold">{t("documents.title")}</h1>
        <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-500">
          {t("documents.subtitle")}
        </p>
      </header>

      <section className="grid gap-4 lg:grid-cols-2">
        <FolderPanel
          subdir="notes"
          heading={t("documents.notesHeading")}
          hint={t("documents.notesHint")}
          promoteEnabled
        />
        <FolderPanel
          subdir="teaching-plans"
          heading={t("documents.teachingPlansHeading")}
          hint={t("documents.teachingPlansHint")}
          promoteEnabled
        />
      </section>

      <section>
        <FolderPanel
          subdir="artifacts"
          heading={t("documents.generatedHeading")}
          hint={t("documents.artifactsHint")}
          promoteEnabled
        />
      </section>

      <section>
        <FilesPanel />
      </section>
    </div>
  );
}

// ── Real files (upload + list from Nova OS) ────────────────────────

function FilesPanel() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [err, setErr] = useState<string | null>(null);
  // Folder navigation: "" = root, otherwise the subdir path we've drilled
  // into (e.g. "artifacts"). preview holds the file being previewed.
  const [currentPath, setCurrentPath] = useState("");
  const [preview, setPreview] = useState<DocEntry | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const files = useQuery({
    // Namespaced key: this browser's fetchFiles() returns a merged
    // DocEntry[] — NOT the DocTree shape FolderPanel caches under
    // ["documents-tree", <subdir>]. Sharing that key poisoned the
    // panels' cache when drilling into e.g. "artifacts": the panel
    // read .files off an array and rendered 暂无文件 (2026-07-28 bug).
    queryKey: ["documents-browser", currentPath],
    queryFn: () => fetchFiles(currentPath),
    refetchInterval: 30_000,
  });

  const upload = useMutation({
    // Batch upload: the input is `multiple`, so uploads run sequentially
    // into the current folder and the list refreshes once at the end.
    mutationFn: async (fs: File[]) => {
      for (const f of fs) {
        await uploadFile(f, currentPath);
        const twin = await searchableTwinOf(f);
        if (twin) await uploadFile(twin, currentPath);
      }
    },
    onMutate: () => setErr(null),
    onError: (e: Error) => setErr(e.message),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["documents-browser"] });
      void qc.invalidateQueries({ queryKey: ["documents-browser"] });
      void qc.invalidateQueries({ queryKey: ["documents-tree"] });
    },
  });

  const remove = useMutation({
    // One path or many — single-row and multi-select delete share this.
    mutationFn: async (paths: string[]) => {
      for (const p of paths) await deleteFile(p);
    },
    onMutate: () => setErr(null),
    onError: (e: Error) => setErr(e.message),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["documents-browser"] });
      void qc.invalidateQueries({ queryKey: ["documents-tree"] });
      setSelected(new Set());
    },
  });

  function toggleSelect(path: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(path)) next.delete(path);
      else next.add(path);
      return next;
    });
  }

  function askDelete(f: DocEntry) {
    const ok = window.confirm(
      f.type === "folder"
        ? t("documents.files.deleteConfirmFolder", { name: f.name })
        : t("documents.files.deleteConfirmFile", { name: f.name }),
    );
    if (!ok) return;
    remove.mutate([f.path]);
  }

  function askDeleteSelected() {
    const paths = [...selected];
    if (!paths.length) return;
    const ok = window.confirm(
      t("documents.files.deleteConfirmMany", { count: paths.length }),
    );
    if (!ok) return;
    remove.mutate(paths);
  }

  function pickFile() {
    inputRef.current?.click();
  }

  function onFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const list = Array.from(e.target.files ?? []);
    if (!list.length) return;
    upload.mutate(list);
    // Reset so re-uploading the same filename still fires.
    e.target.value = "";
  }

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4">
      <header className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-1.5 text-sm font-semibold text-slate-700">
          <button
            type="button"
            onClick={() => setCurrentPath("")}
            disabled={!currentPath}
            className={currentPath ? "text-sky-700 hover:underline" : ""}
          >
            {t("documents.files.heading")}
          </button>
          {currentPath && (
            <>
              <span className="text-slate-400">/</span>
              <span className="text-slate-800">{currentPath}</span>
            </>
          )}
        </div>
        <div className="flex items-center gap-2">
          {selected.size > 0 && (
            <button
              type="button"
              onClick={askDeleteSelected}
              disabled={remove.isPending}
              className="px-3 py-1 text-xs font-medium rounded border border-rose-300 text-rose-600 hover:bg-rose-50 disabled:opacity-50"
            >
              {remove.isPending
                ? t("common.deleting")
                : t("documents.files.deleteSelected", { count: selected.size })}
            </button>
          )}
          <button
            type="button"
            onClick={pickFile}
            disabled={upload.isPending}
            className="px-3 py-1 text-xs font-medium rounded bg-sky-500 text-white hover:bg-sky-600 disabled:opacity-50"
          >
            {upload.isPending ? t("documents.files.uploading") : t("documents.files.uploadButton")}
          </button>
          <input
            ref={inputRef}
            type="file"
            multiple
            className="hidden"
            onChange={onFileChange}
          />
        </div>
      </header>

      {err && (
        <div className="mb-3 rounded border border-rose-200 bg-rose-50 p-2 text-xs text-rose-700">
          {t("documents.files.uploadError", { msg: err })}
        </div>
      )}

      {files.isLoading ? (
        <p className="text-xs text-slate-500 italic">{t("documents.files.loading")}</p>
      ) : files.error ? (
        <p className="text-xs text-rose-600">
          {t("documents.files.loadError", { msg: (files.error as Error).message })}
        </p>
      ) : (files.data?.length ?? 0) === 0 ? (
        <p className="text-xs text-slate-500 italic">
          {t("documents.files.empty")}
        </p>
      ) : (
        <ul className="divide-y divide-slate-100">
          {files.data!.map((f) => (
            <li
              key={f.path}
              className="group flex items-center gap-3 py-2 text-xs"
            >
              <input
                type="checkbox"
                checked={selected.has(f.path)}
                onChange={() => toggleSelect(f.path)}
                className="h-3.5 w-3.5 shrink-0 accent-sky-500"
                aria-label={f.name}
              />
              <span className="text-base">
                {f.type === "folder" ? "📁" : "📄"}
              </span>
              {f.type === "folder" ? (
                <button
                  type="button"
                  onClick={() => setCurrentPath(f.path)}
                  className="flex-1 truncate text-left text-sky-700 hover:underline"
                >
                  {f.name}/
                </button>
              ) : (
                <span className="flex-1 truncate text-slate-800">{f.name}</span>
              )}
              <span className="font-mono text-[10px] text-slate-400 w-20 text-right truncate">
                {f.type === "folder" ? t("documents.files.kindFolder") : t("documents.files.kindFile")}
              </span>
              {f.type === "file" && (
                <>
                  <PromoteFileButton subdir={currentPath} filename={f.name} />
                  <button
                    type="button"
                    onClick={() => setPreview(f)}
                    className="opacity-0 group-hover:opacity-100 text-xs px-2 py-0.5 rounded text-slate-700 hover:bg-slate-100 transition-opacity"
                  >
                    {t("common.view")}
                  </button>
                </>
              )}
              <button
                type="button"
                onClick={() => askDelete(f)}
                disabled={remove.isPending}
                title={t("documents.files.delete")}
                className="opacity-0 group-hover:opacity-100 text-xs px-2 py-0.5 rounded text-rose-600 hover:bg-rose-50 disabled:opacity-30 transition-opacity"
              >
                {t("documents.files.delete")}
              </button>
            </li>
          ))}
        </ul>
      )}

      {preview && (
        <FilePreviewModal
          path={preview.path}
          filename={preview.name}
          onClose={() => setPreview(null)}
        />
      )}
    </div>
  );
}
